// Admin → User Access. The React port of the old Windows
// "Users - Location Mapping" form.
//
// Pick a user, then four transfer panels that cascade: Locations -> Rooms ->
// Devices, with the location's Common Devices alongside. Left is what the user
// COULD be given, right is what they HAVE, and the arrows write to the mapping
// tables. The logic — and the reason a device needs all four rows to be visible
// — lives in core/admin/userAccess.
//
// ⚠️ THIS PAGE WRITES OTHER PEOPLE'S ACCESS. It is Super Admin only, gated here
// and on the route, and every list is re-read after a write rather than patched
// locally: the delete endpoints report row counts belonging to other tables
// entirely, so the list itself is the only honest confirmation.
//
// ── All four panels FIT, and the page never scrolls ────────────────────────
// The whole point of the form is seeing the chain at once — a location, its
// rooms, that room's devices — so a page you have to scroll to reach the device
// panel has lost the thing it was copied for.
//
// They sit in a 2x2 GRID, capped in width and pinned to the left, which is how
// the Windows form laid them out. Three things come out of that shape:
//
//   - each panel is half as wide, so a name has room without the panel taking
//     the whole monitor
//   - two rows instead of four means each panel is TWICE as tall, so more rows
//     are visible, not fewer
//   - the space to the right is left deliberately free. The rest of that form —
//     location wifi, the password, the generate/notification controls — belongs
//     there, and a full-width panel would have to be rebuilt to make room.
//
// ⚠️ 100% AND NOT 100vh, and `minHeight: 0` on every flex ancestor. AppLayout is
// already a 100vh column with a header above this, so 100vh overflows by exactly
// the header's height; and a flex child defaults to min-height auto, so without
// the zero the lists refuse to shrink and push the page long again.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Autocomplete, TextField, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import { getAllUsers, userLabel } from '../../../core/devices/deviceAdmin';
import { tokenFilter } from '../../../core/utils/search';
import TransferList from '../../shared/TransferList';
import {
  loadLocations, loadRooms, loadRoomDevices, loadCommonDevices,
  allotLocation, unallotLocation, allotRoom, unallotRoom,
  allotDevice, unallotDevice, danglingUnder, keepOrFirst,
  reorderLocations, reorderRooms, reorderDevices,
  locationKey, roomKey, deviceKey, installIdOf,
} from '../../../core/admin/userAccess';
import DeviceDetailsPanel from './userAccess/DeviceDetailsPanel';

const EMPTY = { available: [], assigned: [] };

export default function UserAccessScreen() {
  const { isSuperAdmin } = useApp();

  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  const [locations, setLocations] = useState(EMPTY);
  const [rooms, setRooms] = useState(EMPTY);
  const [devices, setDevices] = useState(EMPTY);
  const [common, setCommon] = useState(EMPTY);

  const [location, setLocation] = useState(null);
  const [room, setRoom] = useState(null);
  // The device whose details are shown. Set from either device panel — a common
  // device is as worth inspecting as one in a room.
  const [device, setDevice] = useState(null);
  const [busy, setBusy] = useState('');

  // Confirmation for the one action that leaves rows behind — see danglingUnder.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    getAllUsers()
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load the user list.'));
  }, []);

  const uid = user ? Number(user.UserId) : null;

  // ── Loaders, and the cascade ─────────────────────────────────────────────
  //
  // Choosing a user opens the FIRST allotted location, which opens its first
  // room, which opens its first device. Four clicks to see one device's details
  // is three too many when the usual question is "what can this person reach",
  // and the answer is nearly always visible from the first of everything.
  //
  // Each level's selection is settled by keepOrFirst, so re-reading a list after
  // a write does not throw the operator back to the top — see that function for
  // why neither half of the rule can be dropped.
  //
  // Loaders take the row they are loading FOR as an argument rather than reading
  // it from state: they are called from the level above, in the same tick as its
  // setState, and state would still hold the previous value.
  const refreshDevices = useCallback(async (loc, rm) => {
    if (uid == null || !loc || !rm) { setDevices(EMPTY); setDevice(null); return; }
    setBusy('devices');
    try {
      const next = await loadRoomDevices(uid, loc.LocationID, rm.RoomID);
      setDevices(next);
      setDevice((cur) => keepOrFirst(cur, next.assigned, deviceKey));
    } finally { setBusy(''); }
  }, [uid]);

  const refreshRooms = useCallback(async (loc) => {
    if (uid == null || !loc) { setRooms(EMPTY); setRoom(null); return; }
    setBusy('rooms');
    let next = EMPTY;
    try {
      next = await loadRooms(uid, loc.LocationID);
      setRooms(next);
    } finally { setBusy(''); }
    const rm = keepOrFirst(room, next.assigned, roomKey);
    setRoom(rm);
    refreshDevices(loc, rm);
    // `room` is read, not depended on: adding it would rebuild this callback on
    // every room change and re-run the effect that calls it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, refreshDevices]);

  const refreshCommon = useCallback(async (loc) => {
    if (uid == null || !loc) { setCommon(EMPTY); return; }
    setBusy('common');
    try {
      setCommon(await loadCommonDevices(uid, loc.LocationID));
    } finally { setBusy(''); }
  }, [uid]);

  const refreshLocations = useCallback(async () => {
    if (uid == null) { setLocations(EMPTY); return; }
    setBusy('locations');
    let next = EMPTY;
    try {
      next = await loadLocations(uid);
      setLocations(next);
    } finally { setBusy(''); }
    const loc = keepOrFirst(location, next.assigned, locationKey);
    setLocation(loc);
    if (loc) { refreshRooms(loc); refreshCommon(loc); } else {
      setRooms(EMPTY); setRoom(null); setDevices(EMPTY); setDevice(null); setCommon(EMPTY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, refreshRooms, refreshCommon]);

  useEffect(() => {
    setLocation(null); setRoom(null); setDevice(null);
    setRooms(EMPTY); setDevices(EMPTY); setCommon(EMPTY);
    refreshLocations();
  }, [refreshLocations]);

  const pickLocation = (loc) => {
    setLocation(loc);
    refreshRooms(loc);
    refreshCommon(loc);
  };

  const pickRoom = (rm) => {
    setRoom(rm);
    refreshDevices(location, rm);
  };

  // Every mutation re-reads rather than patching state — the writes cannot be
  // trusted to report what they did.
  const run = async (fn, after) => {
    setError('');
    try {
      await fn();
      await after();
    } catch (e) {
      setError('That change could not be saved. Nothing has been altered.');
    }
  };

  const askUnallotLocation = async (loc) => {
    const counts = await danglingUnder(uid, loc.LocationID).catch(() => null);
    if (counts && (counts.rooms || counts.devices)) {
      setConfirm({ loc, ...counts });
      return;
    }
    run(() => unallotLocation(uid, loc.LocationID), () => refreshLocations());
  };

  if (!isSuperAdmin) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Super Admin only.</Alert></Box>;
  }

  const devTitle = room ? `Devices in ${String(room.RoomName || '').trim()}` : 'Devices';

  return (
    <Box sx={{
      p: 0.75, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.75,
    }}
    >
      {/* Title, picker and the warning on ONE row — three stacked blocks cost
          the device panel its last two rows. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }} noWrap>User Access</Typography>
        <Autocomplete
          size="small"
          sx={{ width: 320 }}
          options={users}
          value={user}
          onChange={(_, v) => setUser(v)}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(o, v) => String(o.UserId) === String(v.UserId)}
          filterOptions={tokenFilter((u) => `${u.Surname || ''} ${u.Name || ''} ${u.FatherHusband || ''} ${u.MobileNumber || ''}`)}
          renderInput={(p) => (
            <TextField {...p} placeholder="Type a name or mobile…" />
          )}
        />
        {user && (
          <Typography color="text.secondary" sx={{ fontSize: 11 }} noWrap>
            {`ID ${user.UserId}${user.MobileNumber ? ` · ${user.MobileNumber}` : ''}`}
            {' — this user’s access, not yours.'}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {error && (
          <Alert severity="error" sx={{ py: 0, fontSize: 11 }}>{error}</Alert>
        )}
      </Box>

      {!user ? (
        <Alert severity="info">Choose a user to see what they can reach.</Alert>
      ) : (
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          // Capped rather than fractional: `1fr` would stretch each panel across
          // half a wide monitor, which is the opposite of the ask. minmax(0, …)
          // is what lets the grid track shrink below its content — without the
          // 0 the columns refuse to narrow and the page scrolls sideways.
          // ⚠️ A COLUMN HOLDS TWO LISTS, NOT ONE. Sizing these as if each panel
          // were a single list is what made Rooms unreadable: 250px looked
          // generous until it was split between two panes and a set of arrows,
          // leaving ~110px a list and truncating "Sanjay Room Passage" to
          // "Sanja...".
          //
          // So the four panels sit two-by-two in EQUAL columns wide enough for
          // two real lists, rather than four unequal ones. Details takes the
          // remaining width and spans both rows — at half height its fields
          // scrolled, and a details panel you have to scroll stops being read.
          //
          // The pairing is CONTAINER on the left, ITS DEVICES on the right:
          //
          //     Locations   |   Common Devices    <- the location's own
          //     Rooms       |   Devices           <- the picked room's
          //
          // so each row answers one question, and the thing you click is
          // alongside what clicking it produced. Reading down the left column
          // is the drill-down; reading across a row is "and what is in it".
          gridTemplateAreas: {
            xs: '"loc" "common" "rooms" "dev" "details"',
            lg: '"loc common details" "rooms dev details"',
          },
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 430px) minmax(0, 430px) minmax(280px, 1fr)',
          },
          gridTemplateRows: { xs: 'repeat(5, minmax(0, 1fr))', lg: 'repeat(2, minmax(0, 1fr))' },
          justifyContent: 'start',
          gap: 0.75,
        }}
        >
          <TransferList
            area="loc"
            title="Locations"
            note="Click an allotted location to load its rooms"
            available={locations.available}
            assigned={locations.assigned}
            keyOf={locationKey}
            labelOf={(l) => l.DisplayName || l.LocationName || `Location ${l.LocationID}`}
            subLabelOf={(l) => `ID ${l.LocationID}`}
            picked={location}
            onPick={pickLocation}
            busy={busy === 'locations'}
            onAllot={(l) => run(
              () => allotLocation(uid, l.LocationID),
              () => refreshLocations(),
            )}
            onUnallot={askUnallotLocation}
            onReorder={(list) => run(
              () => reorderLocations(uid, list),
              () => refreshLocations(),
            )}
          />

          <TransferList
            area="rooms"
            title={location ? `Rooms in ${location.DisplayName || location.LocationName}` : 'Rooms'}
            availableTitle="Unassigned"
            assignedTitle="Assigned"
            // ⚠️ Not a per-user setting — Rooms.SortOrder is one column shared by
            // everyone who can see the room. Said out loud rather than hidden.
            note="Order here is shared by every user of this room"
            available={rooms.available}
            assigned={rooms.assigned}
            keyOf={roomKey}
            labelOf={(r) => String(r.RoomName || '').trim() || `Room ${r.RoomID}`}
            subLabelOf={(r) => `ID ${r.RoomID}`}
            picked={room}
            onPick={pickRoom}
            disabled={!location}
            busy={busy === 'rooms'}
            emptyAvailable={location ? 'Every room here is assigned.' : 'Pick a location first.'}
            emptyAssigned={location ? 'No rooms assigned.' : 'Pick a location first.'}
            onAllot={(r) => run(
              () => allotRoom(uid, location.LocationID, r.RoomID),
              () => refreshRooms(location),
            )}
            onUnallot={(r) => run(
              () => unallotRoom(uid, location.LocationID, r.RoomID),
              () => refreshRooms(location),
            )}
            onReorder={(list) => run(() => reorderRooms(list), () => refreshRooms(location))}
          />

          <TransferList
            area="dev"
            title={devTitle}
            available={devices.available}
            assigned={devices.assigned}
            keyOf={deviceKey}
            labelOf={(d) => d.DeviceName || d.UniqueNumber}
            subLabelOf={(d) => d.UniqueNumber}
            picked={device}
            onPick={setDevice}
            disabled={!room}
            busy={busy === 'devices'}
            emptyAvailable={room ? 'Every device in this room is allotted.' : 'Pick a room first.'}
            emptyAssigned={room ? 'No devices allotted.' : 'Pick a room first.'}
            onAllot={(d) => run(
              () => allotDevice(uid, installIdOf(d)),
              () => refreshDevices(location, room),
            )}
            onUnallot={(d) => run(
              () => unallotDevice(uid, installIdOf(d)),
              () => refreshDevices(location, room),
            )}
            onReorder={(list) => run(
              () => reorderDevices(uid, list),
              () => refreshDevices(location, room),
            )}
          />

          <TransferList
            area="common"
            title="Common Devices"
            availableTitle="Unassigned"
            assignedTitle="Assigned"
            note="Belong to the location, not to any room"
            available={common.available}
            assigned={common.assigned}
            keyOf={deviceKey}
            labelOf={(d) => d.DeviceName || d.UniqueNumber}
            subLabelOf={(d) => d.UniqueNumber}
            picked={device}
            onPick={setDevice}
            disabled={!location}
            busy={busy === 'common'}
            emptyAvailable={location ? 'Every common device is assigned.' : 'Pick a location first.'}
            emptyAssigned={location ? 'No common devices assigned.' : 'Pick a location first.'}
            onAllot={(d) => run(
              () => allotDevice(uid, installIdOf(d)),
              () => refreshCommon(location),
            )}
            onUnallot={(d) => run(
              () => unallotDevice(uid, installIdOf(d)),
              () => refreshCommon(location),
            )}
            onReorder={(list) => run(() => reorderDevices(uid, list), () => refreshCommon(location))}
          />

          <DeviceDetailsPanel device={device} />
        </Box>
      )}

      {/* Taking a location away leaves the room and device rows behind. That is
          not fixed silently in either direction — the count is shown and the
          choice is the operator's. */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Take this location away?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {`${user ? userLabel(user) : 'This user'} still has `}
            <b>{`${confirm?.rooms || 0} room${confirm?.rooms === 1 ? '' : 's'}`}</b>
            {' and '}
            <b>{`${confirm?.devices || 0} device${confirm?.devices === 1 ? '' : 's'}`}</b>
            {` mapped under ${confirm?.loc?.DisplayName || 'it'}.`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Removing the location hides all of it, but leaves those rows in
            place. They do no harm and come back if the location is allotted
            again — but a device mapped to a user who cannot see its location is
            the hardest kind of assignment to explain later. Clear them from the
            panels above first if you want a clean removal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const loc = confirm.loc;
              setConfirm(null);
              run(() => unallotLocation(uid, loc.LocationID), () => refreshLocations());
            }}
          >
            Take it away
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
